import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Sparkles, Mic, MicOff } from "lucide-react";
import { sanitizeLongText } from "@/lib/sanitize";

interface AddLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeadAdded: () => void;
  groupId?: string | null;
}

const AddLeadDialog = ({ open, onOpenChange, onLeadAdded, groupId }: AddLeadDialogProps) => {
  const [conversationText, setConversationText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setLoading(false);
      setConversationText("");
      setIsRecording(false);
      setIsTranscribing(false);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    }
  }, [open]);

  // Lead cap removed — the trial and every paid tier are unlimited.

  const handleVoiceInput = async () => {
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((track) => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast("Recording started", { description: "Speak now. Click the mic again to stop." });
    } catch {
      toast.error("Microphone access denied", {
        description: "Please allow microphone access to use voice input.",
      });
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsRecording(false);
    setIsTranscribing(true);

    toast("Transcribing...", { description: "Processing your audio..." });

    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);

      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(",")[1];

        const { data, error } = await supabase.functions.invoke("transcribe-audio", {
          body: { audio: base64Audio },
        });

        setIsTranscribing(false);

        if (error) throw error;

        if (data?.text) {
          setConversationText((prev) => (prev ? prev + " " + data.text : data.text));
          toast("Transcription complete", { description: "Your speech has been added to the notes." });
        }
      };
    } catch {
      setIsTranscribing(false);
      toast.error("Transcription failed", {
        description: "Failed to transcribe audio. Please try again.",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    try {
      const sanitizedText = sanitizeLongText(conversationText);
      if (!sanitizedText) {
        throw new Error("Please enter valid conversation notes.");
      }

      const { data, error } = await supabase.functions.invoke("process-lead", {
        body: { 
          conversationText: sanitizedText,
          groupId: groupId || null
        },
      });

      if (error) {
        if (error.message?.includes("429")) {
          throw new Error("Rate limit exceeded. Please try again later.");
        }
        if (error.message?.includes("402")) {
          throw new Error("AI credits depleted. Please add credits to continue.");
        }
        throw error;
      }

      toast("Lead added successfully!", {
        description: "The AI has processed your conversation and created a new lead.",
      });

      setConversationText("");
      onOpenChange(false);
      onLeadAdded();
    } catch (error: any) {
      toast.error("Error", {
        description: error.message || "Failed to process lead",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Add Lead with AI
          </DialogTitle>
          <DialogDescription>
            Describe your conversation with the lead, and our AI will automatically extract 
            and organize all the relevant information.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="conversation">Conversation Notes</Label>
              <Button
                type="button"
                variant={isRecording ? "destructive" : "outline"}
                size="sm"
                className="gap-1.5"
                onClick={handleVoiceInput}
                disabled={isTranscribing || loading}
              >
                {isTranscribing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Transcribing...
                  </>
                ) : isRecording ? (
                  <>
                    <MicOff className="h-3.5 w-3.5" />
                    Stop Recording
                  </>
                ) : (
                  <>
                    <Mic className="h-3.5 w-3.5" />
                    Voice Input
                  </>
                )}
              </Button>
            </div>
            <Textarea
              id="conversation"
              placeholder="Example: I spoke with John Smith from Acme Corp today. He's interested in our premium package worth around $50,000. His email is john@acme.com and phone is 555-0123. He wants to see a proposal next week. Priority is high since they need to decide by end of month. Found us through LinkedIn."
              value={conversationText}
              onChange={(e) => setConversationText(e.target.value)}
              className={`min-h-[200px] transition-colors ${isRecording ? "border-destructive ring-1 ring-destructive" : ""}`}
              required
            />
            <p className="text-sm text-muted-foreground">
              {isRecording
                ? "🔴 Recording in progress, speak clearly, then click Stop Recording."
                : "Include details like name, company, contact info, next steps, priority, and how they found you. Or use voice input to dictate."}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || isRecording || isTranscribing} className="gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Processing..." : "Process with AI"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadDialog;